import * as React from 'react';
import './editModalBody.scss';
import { ILabEmployee } from 'typings/employeeErrorData';
import EditModalDropdown from './editModalDropdown';
import EditModalAutoComplete from './editModalAutoComplete';
import { IEmployeeIdMap } from 'typings/employee';
import { getDateLimitForCycle } from 'utils/dateUtils';
import { CustomDatePicker } from 'nomst/Components/Utils/customDatePicker';

interface EditModalBodyProps {
    employeeIds: IEmployeeIdMap;
    email: NullOrUndefined<string>;
    labEmployee: Nullable<ILabEmployee>;
    isSending: boolean;
    errorDetails: { [id: string]: string };
    onUpdateCallBack: (labEmployee: Nullable<ILabEmployee>, errorDetails: { [id: string]: string }) => void
    handleSetWorkEmail: (e: React.ChangeEvent<HTMLInputElement>) => void;
    isEmptyManagerIdValid:  (labEmployee: Nullable<ILabEmployee>) => boolean;
}

export default class EditModalBody extends React.Component<EditModalBodyProps, {}> {
    constructor(props: EditModalBodyProps) {
        super(props);
        this.handleSetFirstName = this.handleSetFirstName.bind(this);
        this.handleSetLastName = this.handleSetLastName.bind(this);
        this.handleSetSwh = this.handleSetSwh.bind(this);
        this.handleHireDateChange = this.handleHireDateChange.bind(this);
        this.renderWarning = this.renderWarning.bind(this);
    }

    render(): React.ReactNode {
        return (
            <div className="nomst-edit-modal-body">
                <div className="nomst-edit-modal-body__left-column">
                    {this.renderFirstName()}
                    <EditModalDropdown
                        type='jobs'
                        labEmployee={this.props.labEmployee}
                        isSending={this.props.isSending}
                        errorDetails={this.props.errorDetails}
                        onUpdateCallBack={this.props.onUpdateCallBack}
                    />
                    {this.renderHireDate()}
                    <EditModalAutoComplete
                        employeeIds={this.props.employeeIds}
                        labEmployee={this.props.labEmployee}
                        isSending={this.props.isSending}
                        errorDetails={this.props.errorDetails}
                        onUpdateCallBack={this.props.onUpdateCallBack}
                        isEmptyManagerIdValid={this.props.isEmptyManagerIdValid}
                    />
                    {this.renderWorkEmail()}
                </div>
                <div className="nomst-edit-modal-body__right-column">
                    {this.renderLastName()}
                    <EditModalDropdown
                        type='payType'
                        labEmployee={this.props.labEmployee}
                        isSending={this.props.isSending}
                        errorDetails={this.props.errorDetails}
                        onUpdateCallBack={this.props.onUpdateCallBack}
                    />
                    {this.renderSwhField()}
                    {this.renderCurrency()}
                </div>
            </div>
        );
    }

    private renderFirstName(): JSX.Element {
        const { labEmployee, errorDetails } = this.props;
        const classNameWithErrorModifier = errorDetails.firstName
            ? 'nomst-edit-modal-body__field--error'
            : '';

        const defaultValue = labEmployee?.firstName ?? '';

        return (
            <div id="firstName" className={`nomst-edit-modal-body__field ${classNameWithErrorModifier}`}>
                {this.renderInput('editFirstName', 'First Name', defaultValue, this.handleSetFirstName)}
                {errorDetails.firstName && this.renderWarning(errorDetails.firstName, 'Enter valid First Name', 'editFirstName')}
            </div>
        );
    }

    private renderInput(
        id: string,
        labelName: string,
        defaultValue: string,
        onSetHandle: ((e: React.ChangeEvent<HTMLInputElement>) => void)): JSX.Element {
        return (
            <>
                <label className="nomst-edit-modal-body__field-label">{labelName}
                    <input
                        id={id}
                        type='text'
                        className="pxl-form-control"
                        value={defaultValue}
                        onChange={onSetHandle}
                        disabled={this.props.isSending}
                    />
                </label>
            </>
        );
    }

    private renderLastName(): JSX.Element {
        const { labEmployee, errorDetails } = this.props;

        const classNameWithErrorModifier = errorDetails.lastName
            ? 'nomst-edit-modal-body__field--error'
            : '';

        const defaultValue = labEmployee?.lastName ?? '';

        return (
            <div id="lastName" className={`nomst-edit-modal-body__field ${classNameWithErrorModifier}`}>
                {this.renderInput('editLastName', 'Last Name', defaultValue, this.handleSetLastName)}
                {errorDetails.lastName && this.renderWarning(errorDetails.lastName, 'Enter valid Last Name', 'editLastName')}
            </div>
        );
    }

    private renderWorkEmail(): JSX.Element {
        const { errorDetails } = this.props;
        const classNameWithErrorModifier = errorDetails.workEmail
            ? 'nomst-edit-modal-body__field--error'
            : '';

        const defaultValue = this.props.email ?? '';

        return (
            <div id="email" className={`nomst-edit-modal-body__field ${classNameWithErrorModifier}`}>
                {this.renderInput('editEmail', 'Email Address', defaultValue, this.props.handleSetWorkEmail)}
                {errorDetails.workEmail && this.renderWarning(errorDetails.workEmail, 'Enter valid Email Address', 'editEmail')}
            </div>
        );
    }

    private renderSwhField(): JSX.Element {
        const { labEmployee, errorDetails } = this.props;

        const classNameWithErrorModifier = errorDetails.standardWorkHours
            ? 'nomst-edit-modal-body__field--error'
            : '';

        const defaultValue = labEmployee?.standardWorkHours ?? '';
        const error = errorDetails.standardWorkHours || (labEmployee?.standardWorkHours ? '' : 'Missing standard work hours');

        return (
            <div id="swh" className={`nomst-edit-modal-body__field ${classNameWithErrorModifier}`}>
                <label htmlFor="editSwh" className="nomst-edit-modal-body__field-label">Standard Work Hours</label>
                <input
                    id="editSwh"
                    type='number'
                    className="pxl-form-control"
                    placeholder="0.00"
                    step="0.25"
                    value={defaultValue}
                    onChange={this.handleSetSwh}
                    disabled={this.props.isSending}
                    min="0.00"
                    max="168.00"
                />
                {error && this.renderWarning(error, 'Enter valid Standard Work Hours', 'editSwh')}
            </div>
        );
    }

    private renderCurrency(): JSX.Element {
        const { labEmployee, errorDetails } = this.props;
        const defaultValue = labEmployee?.currency ?? '';

        return (
            <div id="currency" className="nomst-edit-modal-body__field">
                <label htmlFor="editCurrency" className="nomst-edit-modal-body__field-label">Currency</label>
                <input
                    id="editCurrency"
                    type='text'
                    className="pxl-form-control pxl-form-control--resize"
                    value={defaultValue}
                    disabled={true}
                />
                {errorDetails.currency && this.renderWarning(errorDetails.currency, 'Select valid job with currency', 'editCurrency')}
            </div>
        );
    }

    private renderHireDate(): JSX.Element {
        const { labEmployee, errorDetails } = this.props;

        const classNameWithErrorModifier = errorDetails.dateOfHire
            ? 'nomst-edit-modal-body__field--error'
            : '';
        const maxDateLimt = getDateLimitForCycle(true, 100);
        const minDateLimit = getDateLimitForCycle(false, 100);

        return (
            <div id="hireDate" className={`nomst-edit-modal-body__field ${classNameWithErrorModifier}`}>
                <label htmlFor="editHireDate" className="nomst-edit-modal-body__field-label">Hire Date</label>
                <CustomDatePicker
                    id="editHireDate"
                    value={labEmployee?.dateOfHire}
                    onChange={this.handleHireDateChange}
                    maxDateValue={maxDateLimt}
                    minDateValue={minDateLimit}
                />
                {errorDetails.dateOfHire && this.renderWarning(errorDetails.dateOfHire, 'Select Hire Date', 'editHireDate')}
            </div>
        );
    }

    private handleSetSwh(e: React.ChangeEvent<HTMLInputElement>): void {
        const { labEmployee } = { ...this.props };
        if (e?.target?.value != null && labEmployee) {
            const swh: number = parseFloat(e.target.value);
            labEmployee.standardWorkHours = swh;
            let { errorDetails } = { ...this.props };
            if(labEmployee?.standardWorkHours) {
                delete errorDetails['standardWorkHours'];
            } else {
                errorDetails = Object.assign( errorDetails, {'standardWorkHours':'missing'} );
            }
            this.props.onUpdateCallBack(labEmployee, errorDetails);
        }
    }

    private handleHireDateChange(value: Date): void {
        const { labEmployee } = { ...this.props };
        if(labEmployee) {
            labEmployee.dateOfHire = value;
            let { errorDetails } = {...this.props};
            if(labEmployee?.dateOfHire) {
                delete errorDetails['dateOfHire'];
            } else {
                errorDetails = Object.assign( errorDetails, {'dateOfHire':'missing'} );
            }
            this.props.onUpdateCallBack(labEmployee, errorDetails);
        }
    }

    private handleSetFirstName(e: React.ChangeEvent<HTMLInputElement>): void {
        const { labEmployee } = { ...this.props };
        if (e?.target?.value != null && labEmployee) {
            let { errorDetails } = { ...this.props };
            labEmployee.firstName = e.target.value;
            if(labEmployee?.firstName.length) {
                delete errorDetails['firstName'];
            } else {
                errorDetails = Object.assign( errorDetails, {'firstName':'missing'} );
            }
            this.props.onUpdateCallBack(labEmployee, errorDetails);
        }
    }

    private handleSetLastName(e: React.ChangeEvent<HTMLInputElement>): void {
        const { labEmployee } = { ...this.props };
        if (e?.target?.value != null && labEmployee) {
            let { errorDetails } = { ...this.props };
            labEmployee.lastName = e.target.value;
            if(labEmployee?.lastName.length) {
                delete errorDetails['lastName'];
            } else {
                errorDetails = Object.assign( errorDetails, {'lastName':'missing'} );
            }
            this.props.onUpdateCallBack(labEmployee, errorDetails);
        }
    }

    private renderWarning(
        error: Nullable<string>,
        errorMessage: Nullable<string>,
        htmlFor: string): JSX.Element {
        const errorToDisplay = error ? errorMessage : '';

        return (
            <label htmlFor={htmlFor} className="nomst-edit-modal-body__field-warning">{errorToDisplay}</label>
        );
    }
}
